import {
  isCustomerFieldAnswered,
  type CheckoutCustomerField,
} from "@baluu/ui/components/checkout/customer-fields";
import { validateEmail, validatePhone } from "@baluu/ui/components/checkout/validation";

/**
 * The school's checkout customer fields ("Your information") as both basket
 * checkouts handle them: the widget's full-page checkout and the
 * webcomponents overlay Website v2 checks out through. Everything here is the
 * part that must not differ between the two — which answers go on the wire,
 * in what shape, and which ones stop payment — so it lives once.
 *
 * Answers are held as a single `slug -> raw string` map (checkbox "true" /
 * "false", multiSelect a JSON-array string), exactly what
 * `CheckoutCustomerFields` reads and writes.
 */

/**
 * Where the `GET /widget/customer-fields` fetch stands. "loaded" is the ONLY
 * state in which the `customerFieldAnswers` key goes on the checkout payload
 * and required answers are enforced: a failed fetch degrades to the server's
 * legacy behaviour (answers absent → required-when-absent skipped) rather than
 * submitting an empty list that would trip required fields the buyer never
 * saw. "idle" (still loading) blocks submit — see CUSTOMER_FIELDS_LOADING_ERROR.
 */
export type CustomerFieldsStatus = "idle" | "loaded" | "failed";

/** Shown when the buyer submits before the fields have arrived. */
export const CUSTOMER_FIELDS_LOADING_ERROR =
  "Still loading checkout details — please try again in a moment";

/** How long either checkout waits for the fields before treating the fetch as
 *  failed (a hang would otherwise hold submit on "idle" forever). */
export const CUSTOMER_FIELDS_FETCH_TIMEOUT_MS = 10_000;

export type CustomerFieldLookups = {
  customerFieldTypeBySlug: Map<string, CheckoutCustomerField["fieldType"]>;
  customerFieldOptionsBySlug: Map<string, Set<string>>;
};

/** Per-slug type and currently-declared options, which the payload builder reads. */
export function customerFieldLookups(
  customerFields: CheckoutCustomerField[],
): CustomerFieldLookups {
  return {
    customerFieldTypeBySlug: new Map(customerFields.map((f) => [f.slug, f.fieldType])),
    customerFieldOptionsBySlug: new Map(
      customerFields.map((f) => [f.slug, new Set(f.options ?? [])]),
    ),
  };
}

/**
 * The fields and the buyer-phone row out of a `GET /widget/customer-fields`
 * body. Anything malformed reads as no fields, which is how both checkouts
 * have always treated it.
 */
export function readCustomerFieldsResponse<Phone = unknown>(
  body: unknown,
): { fields: CheckoutCustomerField[]; buyerPhone: Phone | null } {
  const b = (body ?? {}) as { data?: unknown; buyer?: { phone?: Phone } };
  return {
    fields: Array.isArray(b.data) ? (b.data as CheckoutCustomerField[]) : [],
    buyerPhone: b.buyer?.phone ?? null,
  };
}

/**
 * Order-level customer-field answers (sibling of `buyer`, not per-item).
 * Most values pass through as the strings already held in state, but two
 * types are converted to the shape the API contract expects: checkbox →
 * real boolean, and multiSelect → a real array (the server's multiSelect
 * validator expects an array; state holds a JSON-array string, parsed
 * defensively with a [] fallback so bad JSON never crashes checkout).
 * Scoped to slugs actually rendered for the CURRENT basket — belt and
 * braces against the answers state ever carrying a stale slug from a
 * previous basket composition through to the wire.
 */
export function buildCustomerFieldAnswersPayload({
  customerFields,
  customerFieldAnswers,
  customerFieldTypeBySlug,
  customerFieldOptionsBySlug,
}: {
  customerFields: CheckoutCustomerField[];
  customerFieldAnswers: Record<string, string>;
} & CustomerFieldLookups) {
  const renderedFieldSlugs = new Set(customerFields.map((f) => f.slug));
  return Object.entries(customerFieldAnswers)
    .filter(([slug, value]) => renderedFieldSlugs.has(slug) && value !== undefined && value !== "")
    .map(([slug, value]) => {
      const fieldType = customerFieldTypeBySlug.get(slug);
      if (fieldType === "checkbox") {
        return { slug, value: value === "true" };
      }
      if (fieldType === "multiSelect") {
        let parsed: string[] = [];
        try {
          const arr = JSON.parse(value);
          if (Array.isArray(arr)) parsed = arr;
        } catch {
          // malformed JSON — send an empty selection rather than throw
        }
        // Drop any selection the field no longer declares (e.g. a
        // prefilled option the school later removed) so the server's
        // per-option check can't 400 the checkout on a value the buyer
        // has no way to untick.
        const declaredOptions = customerFieldOptionsBySlug.get(slug);
        if (declaredOptions) {
          parsed = parsed.filter((opt) => declaredOptions.has(opt));
        }
        return { slug, value: parsed };
      }
      return { slug, value };
    });
}

/**
 * The `customerFieldAnswers` value for a checkout POST: the answers when the
 * fields loaded, otherwise `undefined` so `JSON.stringify` drops the key and
 * the server treats the order like a client that never rendered the fields
 * (see CustomerFieldsStatus).
 */
export function customerFieldAnswersForPayload(
  customerFields: CheckoutCustomerField[],
  customerFieldAnswers: Record<string, string>,
  status: CustomerFieldsStatus,
): ReturnType<typeof buildCustomerFieldAnswersPayload> | undefined {
  if (status !== "loaded") return undefined;
  return buildCustomerFieldAnswersPayload({
    customerFields,
    customerFieldAnswers,
    ...customerFieldLookups(customerFields),
  });
}

/** Mirrors the server's URL coercion, so a scheme-less "example.com" is caught
 *  before it 400s the checkout mid-payment. */
function isValidHttpUrl(raw: string): boolean {
  try {
    const u = new URL(raw.trim());
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

/**
 * The first reason the buyer's answers can't be submitted, or null. Runs only
 * once the fields have loaded (see CustomerFieldsStatus — a failed fetch must
 * never block payment). Any FILLED url / email / phone answer is
 * format-checked whether or not it is required (the free-text types whose bad
 * input the control can't prevent); required fields use the renderer's own
 * per-type rule (`isCustomerFieldAnswered`), so this gate, the dashboard's
 * preview and the server's `validateCustomerFieldAnswers` agree.
 */
export function validateCustomerFieldAnswers(
  customerFields: CheckoutCustomerField[],
  customerFieldAnswers: Record<string, string>,
  status: CustomerFieldsStatus,
): string | null {
  if (status !== "loaded") return null;
  for (const field of customerFields) {
    const answer = customerFieldAnswers[field.slug];
    const filled = typeof answer === "string" && answer.trim() !== "";

    if (field.fieldType === "url" && filled && !isValidHttpUrl(answer)) {
      return `${field.label} must be a valid link starting with http:// or https://`;
    }
    if (field.fieldType === "email" && filled && !validateEmail(answer).isValid) {
      return `${field.label} must be a valid email address`;
    }
    if (field.fieldType === "phone" && filled && !validatePhone(answer).isValid) {
      return `${field.label} must be a valid phone number`;
    }

    if (!field.isRequired) continue;
    if (!isCustomerFieldAnswered(field, answer)) {
      return `${field.label} is required`;
    }
  }
  return null;
}

type ItemWithAttendees = {
  participants?: ReadonlyArray<{
    isSelf?: boolean;
    linkedAnswers?: Record<string, unknown>;
  }>;
};

/**
 * Questions the buyer already answered on their own ("You") attendee card
 * that are linked to a customer field, keyed by the CUSTOMER slug and held as
 * answer strings. Presentation and seeding only — these are shown back instead
 * of asked again, and the server recomputes the same merge from the self
 * entries at charge time. Scoped to the fields rendered for this basket, so
 * an answer for a course that isn't in it never seeds state.
 */
export function linkedAnswersFromSelfAttendees(
  items: ReadonlyArray<ItemWithAttendees>,
  customerFields: CheckoutCustomerField[],
): Record<string, string> {
  const out: Record<string, string> = {};
  const rendered = new Set(customerFields.map((f) => f.slug));
  for (const item of items) {
    for (const participant of item.participants ?? []) {
      if (!participant.isSelf || !participant.linkedAnswers) continue;
      for (const [slug, value] of Object.entries(participant.linkedAnswers)) {
        if (!rendered.has(slug)) continue;
        if (value === undefined || value === null || value === "") continue;
        out[slug] = Array.isArray(value) ? JSON.stringify(value) : String(value);
      }
    }
  }
  return out;
}

/**
 * The answers map with a signed-in customer's saved values filled in: only
 * for fields rendered for this basket (the saved-values endpoint returns every
 * field ever answered for the school), and only where the buyer hasn't typed
 * anything. Returns `prev` itself when nothing changes.
 */
export function seedSavedCustomerFieldValues(
  prev: Record<string, string>,
  savedValues: Record<string, unknown>,
  customerFields: CheckoutCustomerField[],
): Record<string, string> {
  const rendered = new Set(customerFields.map((f) => f.slug));
  let next: Record<string, string> | null = null;
  for (const [slug, value] of Object.entries(savedValues)) {
    if (!rendered.has(slug)) continue;
    if (value === undefined || value === null) continue;
    if (prev[slug] !== undefined && prev[slug] !== "") continue;
    next ??= { ...prev };
    next[slug] = Array.isArray(value) ? JSON.stringify(value) : String(value);
  }
  return next ?? prev;
}

/** Display form of a linked answer string (a multiSelect's JSON array reads as a list). */
export function formatLinkedCustomerFieldValue(value: string): string {
  if (!value.startsWith("[")) return value;
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.join(", ") : value;
  } catch {
    return value;
  }
}
